import { Checkbox } from "@/components/ui/checkbox";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Clock, CheckCircle } from "lucide-react";
import {
  useCreateShipmentChangeRequest,
  type ShipmentChangeRequestType,
} from "@/api/shipment-change-requests";
import { toast } from "sonner";
import type { Shipment, ShipmentAddress } from "@/api.types.v2";

interface ShipmentChangeRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: Shipment;
}

interface ChangeTypeOption {
  value: ShipmentChangeRequestType;
  label: string;
  description: string;
  addressType: "FROM" | "TO";
}

const CHANGE_TYPE_OPTIONS: ChangeTypeOption[] = [
  {
    value: "RECIPIENT_DELIVERY_INSTRUCTIONS",
    label: "Update delivery instructions",
    description: "Auto-approved",
    addressType: "TO",
  },
  {
    value: "RECIPIENT_CONTACT_UPDATE",
    label: "Update recipient contact",
    description: "Auto-approved",
    addressType: "TO",
  },
  {
    value: "RECIPIENT_ADDRESS_CORRECTION",
    label: "Correct recipient unit / apartment",
    description: "Requires review",
    addressType: "TO",
  },
  {
    value: "SENDER_PICKUP_INSTRUCTIONS",
    label: "Update pickup instructions",
    description: "Auto-approved",
    addressType: "FROM",
  },
  {
    value: "SENDER_CONTACT_UPDATE",
    label: "Update sender contact",
    description: "Auto-approved",
    addressType: "FROM",
  },
  {
    value: "SENDER_ADDRESS_CORRECTION",
    label: "Correct sender unit / apartment",
    description: "Requires review",
    addressType: "FROM",
  },
];

const AUTO_APPROVED_TYPES: ShipmentChangeRequestType[] = [
  "RECIPIENT_DELIVERY_INSTRUCTIONS",
  "RECIPIENT_CONTACT_UPDATE",
  "SENDER_PICKUP_INSTRUCTIONS",
  "SENDER_CONTACT_UPDATE",
];

function willBeAutoApproved(types: ShipmentChangeRequestType[]): boolean {
  return types.every((t) => AUTO_APPROVED_TYPES.includes(t));
}

function formatAddress(addr?: ShipmentAddress): string {
  if (!addr) return "";
  const a = addr.address;
  return [
    addr.addressLine1 ?? a?.line1,
    addr.addressLine2 ?? a?.line2,
    addr.addressCity ?? a?.city,
    addr.addressProvince ?? a?.province,
    addr.addressPostalCode ?? a?.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
}

// Helper to get contact fields
function getContactFromAddress(addr?: ShipmentAddress) {
  if (!addr) return { name: "", phone: "", email: "", instructions: "" };
  return {
    name: addr.contactName || "",
    phone: addr.contactPhone || "",
    email: addr.contactEmail || "",
    instructions: addr.instructions || "",
  };
}

function physicalDraft(address?: ShipmentAddress) {
  return {
    line1: address?.addressLine1 ?? address?.address?.line1 ?? "",
    city: address?.addressCity ?? address?.address?.city ?? "",
    province: address?.addressProvince ?? address?.address?.province ?? "",
    country: address?.addressCountry ?? address?.address?.country ?? "",
    postalCode:
      address?.addressPostalCode ?? address?.address?.postalCode ?? "",
    plusCode: address?.addressPlusCode ?? address?.address?.plusCode ?? "",
    latitude:
      (
        address?.addressLocation ?? address?.address?.location
      )?.lat?.toString() ?? "",
    longitude:
      (
        address?.addressLocation ?? address?.address?.location
      )?.lng?.toString() ?? "",
  };
}
const PHYSICAL_FIELDS = [
  ["line1", "Street address"],
  ["city", "City"],
  ["province", "Province / state"],
  ["country", "Country"],
  ["postalCode", "Postal code"],
  ["plusCode", "Plus Code"],
  ["latitude", "Latitude"],
  ["longitude", "Longitude"],
] as const;

export function ShipmentChangeRequestDialog({
  open,
  onOpenChange,
  shipment,
}: ShipmentChangeRequestDialogProps) {
  const [pickupVisitOnly, setPickupVisitOnly] = useState(false);
  const [addressDraft, setAddressDraft] = useState(() => physicalDraft());
  const [selectedType, setSelectedType] =
    useState<ShipmentChangeRequestType | null>(null);
  const [formData, setFormData] = useState({
    instructions: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    addressLine2Correction: "",
    additionalNotes: "",
  });

  // Get addresses from shipment
  const fromAddress = shipment.addresses?.find((a) => a.type === "FROM");
  const toAddress = shipment.addresses?.find((a) => a.type === "TO");

  // Get the relevant address based on selected type
  const selectedOption = CHANGE_TYPE_OPTIONS.find(
    (o) => o.value === selectedType
  );
  const currentAddress =
    selectedOption?.addressType === "FROM" ? fromAddress : toAddress;

  // Get contact info from address
  const currentContact = useMemo(
    () => getContactFromAddress(currentAddress),
    [currentAddress]
  );

  // Original values for comparison
  const originalValues = useMemo(() => {
    if (!currentAddress) return null;
    return {
      instructions:
        selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS"
          ? (shipment.deliveryNotes ?? "")
          : currentContact.instructions,
      contactName: currentContact.name,
      contactPhone: currentContact.phone,
      contactEmail: currentContact.email,
      unit: currentAddress.addressLine2 ?? currentAddress.address?.line2 ?? "",
    };
  }, [currentAddress, currentContact, selectedType, shipment.deliveryNotes]);

  const selectChangeType = (next: ShipmentChangeRequestType) => {
    const addressType = CHANGE_TYPE_OPTIONS.find(
      (o) => o.value === next
    )?.addressType;
    const address = addressType === "FROM" ? fromAddress : toAddress;
    const contact = getContactFromAddress(address);
    setPickupVisitOnly(false);
    setFormData({
      instructions:
        next === "RECIPIENT_DELIVERY_INSTRUCTIONS"
          ? (shipment.deliveryNotes ?? "")
          : contact.instructions,
      contactName: contact.name,
      contactPhone: contact.phone,
      contactEmail: contact.email,
      addressLine2Correction:
        address?.addressLine2 ?? address?.address?.line2 ?? "",
      additionalNotes: "",
    });
    setAddressDraft(physicalDraft(address));
    setSelectedType(next);
  };

  const createChangeRequest = useCreateShipmentChangeRequest();

  const resetForm = () => {
    setSelectedType(null);
    setFormData({
      instructions: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      addressLine2Correction: "",
      additionalNotes: "",
    });
  };

  const physicalChanged =
    JSON.stringify(addressDraft) !==
    JSON.stringify(physicalDraft(currentAddress));
  const physicalValid =
    [
      addressDraft.line1,
      addressDraft.city,
      addressDraft.province,
      addressDraft.country,
    ].every((value) => value.trim()) &&
    ((!addressDraft.latitude && !addressDraft.longitude) ||
      (addressDraft.latitude !== "" &&
        addressDraft.longitude !== "" &&
        Number.isFinite(Number(addressDraft.latitude)) &&
        Number.isFinite(Number(addressDraft.longitude)) &&
        Math.abs(Number(addressDraft.latitude)) <= 90 &&
        Math.abs(Number(addressDraft.longitude)) <= 180));
  // Check if data has changed
  const hasChanges = useMemo(() => {
    if (!selectedType || !originalValues) return false;

    if (
      selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS" ||
      selectedType === "SENDER_PICKUP_INSTRUCTIONS"
    ) {
      return formData.instructions !== originalValues.instructions;
    }

    if (
      selectedType === "RECIPIENT_CONTACT_UPDATE" ||
      selectedType === "SENDER_CONTACT_UPDATE"
    ) {
      return (
        formData.contactName !== originalValues.contactName ||
        formData.contactPhone !== originalValues.contactPhone ||
        formData.contactEmail !== originalValues.contactEmail
      );
    }

    if (
      selectedType === "RECIPIENT_ADDRESS_CORRECTION" ||
      selectedType === "SENDER_ADDRESS_CORRECTION"
    ) {
      return pickupVisitOnly
        ? physicalValid
        : physicalChanged
          ? physicalValid
          : formData.addressLine2Correction.trim() !== originalValues.unit;
    }

    return false;
  }, [
    selectedType,
    formData,
    originalValues,
    physicalChanged,
    physicalValid,
    pickupVisitOnly,
  ]);

  const handleSubmit = () => {
    if (!selectedType || !hasChanges) return;

    createChangeRequest.mutate(
      {
        shipmentId: shipment.id,
        requestTypes: [selectedType],
        addressCandidate:
          !pickupVisitOnly &&
          physicalChanged &&
          selectedType.includes("ADDRESS_CORRECTION")
            ? {
                line1: addressDraft.line1.trim(),
                line2: formData.addressLine2Correction.trim(),
                city: addressDraft.city.trim(),
                province: addressDraft.province.trim(),
                country: addressDraft.country.trim(),
                postalCode: addressDraft.postalCode.trim() || null,
                plusCode: addressDraft.plusCode.trim() || null,
                location:
                  addressDraft.latitude && addressDraft.longitude
                    ? {
                        lat: Number(addressDraft.latitude),
                        lng: Number(addressDraft.longitude),
                      }
                    : null,
              }
            : undefined,
        pickupVisitAddressCandidate: pickupVisitOnly
          ? {
              line1: addressDraft.line1.trim(),
              line2: formData.addressLine2Correction.trim() || null,
              city: addressDraft.city.trim(),
              province: addressDraft.province.trim(),
              country: addressDraft.country.trim(),
              postalCode: addressDraft.postalCode.trim() || null,
              plusCode: addressDraft.plusCode.trim() || null,
              location:
                addressDraft.latitude && addressDraft.longitude
                  ? {
                      lat: Number(addressDraft.latitude),
                      lng: Number(addressDraft.longitude),
                    }
                  : null,
            }
          : undefined,
        deliveryInstructions:
          selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS" ||
          selectedType === "SENDER_PICKUP_INSTRUCTIONS"
            ? formData.instructions
            : undefined,
        contactPhone:
          selectedType === "RECIPIENT_CONTACT_UPDATE" ||
          selectedType === "SENDER_CONTACT_UPDATE"
            ? formData.contactPhone
            : undefined,
        contactName:
          selectedType === "RECIPIENT_CONTACT_UPDATE" ||
          selectedType === "SENDER_CONTACT_UPDATE"
            ? formData.contactName
            : undefined,
        contactEmail:
          selectedType === "RECIPIENT_CONTACT_UPDATE" ||
          selectedType === "SENDER_CONTACT_UPDATE"
            ? formData.contactEmail
            : undefined,
        addressLine2Correction:
          selectedType === "RECIPIENT_ADDRESS_CORRECTION" ||
          selectedType === "SENDER_ADDRESS_CORRECTION"
            ? physicalChanged || pickupVisitOnly
              ? undefined
              : formData.addressLine2Correction.trim()
            : undefined,
        additionalNotes: formData.additionalNotes || undefined,
      },
      {
        onSuccess: (data) => {
          if (data.autoApproved) {
            toast.success("Changes applied", {
              description: `Applied: ${data.appliedChanges?.join(", ")}`,
            });
          } else {
            toast.success("Request submitted", {
              description: "The change request has been submitted for review.",
            });
          }
          resetForm();
          onOpenChange(false);
        },
        onError: (error) => {
          const response = error as {
            response?: { data?: { message?: string } };
          };
          toast.error("Failed to submit request", {
            description: response.response?.data?.message,
          });
        },
      }
    );
  };

  const isAutoApproved = selectedType
    ? willBeAutoApproved([selectedType])
    : false;

  // Determine which fields to show
  const showInstructionsField =
    selectedType === "RECIPIENT_DELIVERY_INSTRUCTIONS" ||
    selectedType === "SENDER_PICKUP_INSTRUCTIONS";
  const showContactFields =
    selectedType === "RECIPIENT_CONTACT_UPDATE" ||
    selectedType === "SENDER_CONTACT_UPDATE";
  const showAddressField =
    selectedType === "RECIPIENT_ADDRESS_CORRECTION" ||
    selectedType === "SENDER_ADDRESS_CORRECTION";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetForm();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Request Changes
            {shipment.shipmentNumber && (
              <span className="text-muted-foreground font-normal ml-2">
                SHP-{shipment.shipmentNumber}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Select a change type and update the information.
          </DialogDescription>
        </DialogHeader>

        {/* Current Shipment Details */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {/* Sender */}
          <div className="bg-muted/50 p-3 rounded-md space-y-1">
            <p className="font-medium text-muted-foreground">Sender</p>
            {getContactFromAddress(fromAddress).name && (
              <p>{getContactFromAddress(fromAddress).name}</p>
            )}
            {getContactFromAddress(fromAddress).phone && (
              <p className="text-muted-foreground">
                {getContactFromAddress(fromAddress).phone}
              </p>
            )}
            <p className="text-muted-foreground text-xs">
              {formatAddress(fromAddress)}
            </p>
            {getContactFromAddress(fromAddress).instructions && (
              <p className="text-muted-foreground text-xs italic">
                "{getContactFromAddress(fromAddress).instructions}"
              </p>
            )}
          </div>
          {/* Recipient */}
          <div className="bg-muted/50 p-3 rounded-md space-y-1">
            <p className="font-medium text-muted-foreground">Recipient</p>
            {getContactFromAddress(toAddress).name && (
              <p>{getContactFromAddress(toAddress).name}</p>
            )}
            {getContactFromAddress(toAddress).phone && (
              <p className="text-muted-foreground">
                {getContactFromAddress(toAddress).phone}
              </p>
            )}
            <p className="text-muted-foreground text-xs">
              {formatAddress(toAddress)}
            </p>
            {shipment.deliveryNotes && (
              <p className="text-muted-foreground text-xs italic">
                "{shipment.deliveryNotes}"
              </p>
            )}
          </div>
        </div>

        {/* Change Type Selection */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>What would you like to change?</Label>
            <Select
              value={selectedType || ""}
              onValueChange={(v) =>
                selectChangeType(v as ShipmentChangeRequestType)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select change type" />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  Recipient / Delivery
                </div>
                {CHANGE_TYPE_OPTIONS.filter((o) => o.addressType === "TO").map(
                  (option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <span>{option.label}</span>
                        <span className="text-xs text-muted-foreground">
                          ({option.description})
                        </span>
                      </div>
                    </SelectItem>
                  )
                )}
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-1">
                  Sender / Pickup
                </div>
                {CHANGE_TYPE_OPTIONS.filter(
                  (o) => o.addressType === "FROM"
                ).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">
                        ({option.description})
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Dynamic Fields */}
        {selectedType && (
          <div className="space-y-4 pt-4 border-t">
            {showInstructionsField && (
              <div className="space-y-2">
                <Label htmlFor="instructions">
                  {selectedType === "SENDER_PICKUP_INSTRUCTIONS"
                    ? "Pickup Instructions"
                    : "Delivery Instructions"}
                </Label>
                <Textarea
                  id="instructions"
                  placeholder={
                    selectedType === "SENDER_PICKUP_INSTRUCTIONS"
                      ? "e.g., Ring doorbell, leave at side door..."
                      : "e.g., Leave at front door, call on arrival..."
                  }
                  value={formData.instructions}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      instructions: e.target.value,
                    }))
                  }
                />
                {originalValues?.instructions &&
                  formData.instructions === originalValues.instructions && (
                    <p className="text-xs text-muted-foreground">
                      No changes made
                    </p>
                  )}
              </div>
            )}

            {showContactFields && (
              <div className="space-y-3">
                <Label>Contact Information</Label>
                <div className="space-y-2">
                  <div>
                    <Label
                      htmlFor="contactName"
                      className="text-xs text-muted-foreground"
                    >
                      Name
                    </Label>
                    <Input
                      id="contactName"
                      placeholder="Contact name"
                      value={formData.contactName}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          contactName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="contactPhone"
                      className="text-xs text-muted-foreground"
                    >
                      Phone
                    </Label>
                    <Input
                      id="contactPhone"
                      placeholder="Phone number"
                      value={formData.contactPhone}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          contactPhone: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="contactEmail"
                      className="text-xs text-muted-foreground"
                    >
                      Email
                    </Label>
                    <Input
                      id="contactEmail"
                      placeholder="Email address"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          contactEmail: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {showAddressField && (
              <div className="space-y-2">
                {selectedType === "SENDER_ADDRESS_CORRECTION" &&
                  shipment.pickupBookingId && (
                    <Label className="flex gap-2">
                      <Checkbox
                        checked={pickupVisitOnly}
                        onCheckedChange={(checked) =>
                          setPickupVisitOnly(checked === true)
                        }
                      />
                      Correct the actual collection visit only; keep the label
                      sender unchanged
                    </Label>
                  )}
                {PHYSICAL_FIELDS.map(([field, label]) => (
                  <div key={field}>
                    <Label htmlFor={`physical-${field}`}>{label}</Label>
                    <Input
                      id={`physical-${field}`}
                      value={addressDraft[field]}
                      onChange={(event) =>
                        setAddressDraft((previous) => ({
                          ...previous,
                          [field]: event.target.value,
                          ...([
                            "line1",
                            "city",
                            "province",
                            "country",
                            "postalCode",
                          ].includes(field)
                            ? { latitude: "", longitude: "", plusCode: "" }
                            : {}),
                        }))
                      }
                    />
                  </div>
                ))}
                <Label htmlFor="address">Correct unit / apartment number</Label>
                <Input
                  id="address"
                  placeholder="e.g., Unit 4305"
                  maxLength={100}
                  value={formData.addressLine2Correction}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      addressLine2Correction: e.target.value,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Changes go through review. New labels, collection visits and
                  delivery work are reconciled before the correction is applied.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any other information..."
                value={formData.additionalNotes}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    additionalNotes: e.target.value,
                  }))
                }
              />
              {showAddressField &&
                formData.additionalNotes.trim() &&
                !hasChanges && (
                  <p className="text-xs text-amber-700">
                    Notes alone do not change the address. Edit the unit field
                    above to submit.
                  </p>
                )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {selectedType && (
            <div className="flex-1 text-sm">
              {isAutoApproved ? (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  Will be applied immediately
                </span>
              ) : (
                <span className="text-yellow-600 flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Requires review
                </span>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !selectedType || !hasChanges || createChangeRequest.isPending
              }
            >
              {createChangeRequest.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : !hasChanges && selectedType ? (
                "No changes"
              ) : (
                "Submit Request"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
